
import React from 'react';
import { InsuranceAnalysisResult, Language } from '@/types/insurance';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface InsuranceComparisonPreviewProps {
  content: string;
  analysis: InsuranceAnalysisResult | null;
}

const InsuranceComparisonPreview: React.FC<InsuranceComparisonPreviewProps> = ({ content, analysis }) => {
  const [expanded, setExpanded] = React.useState(false);

  const toggleExpanded = () => setExpanded(prev => !prev);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow overflow-hidden bg-white">
        <ScrollArea className="h-full">
          <div className="p-6" dangerouslySetInnerHTML={{ __html: content }} />
        </ScrollArea>
      </div>
      
      {analysis && (
        <Card className="border-t border-gray-200 rounded-none">
          <div 
            className="p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={toggleExpanded}
          >
            <h3 className="text-base font-medium">Analyse Details</h3>
            {expanded ? (
              <ChevronUp className="h-4 w-4 text-gray-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-gray-500" />
            )}
          </div>
          
          {expanded && (
            <CardContent className="pt-2">
              <div className="space-y-3">
                {analysis.summary && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Zusammenfassung</h4>
                    <p className="text-sm text-gray-700">{analysis.summary}</p>
                  </div>
                )}
                
                {analysis.coverage && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Deckung</h4>
                    <p className="text-sm text-gray-700">{analysis.coverage}</p>
                  </div>
                )}
                
                {analysis.premium && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Prämie</h4>
                    <p className="text-sm text-gray-700">{analysis.premium}</p>
                  </div>
                )}
                
                {analysis.duration && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Laufzeit</h4>
                    <p className="text-sm text-gray-700">{analysis.duration}</p>
                  </div>
                )}
                
                {analysis.benefits && analysis.benefits.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Vorteile</h4>
                    <ul className="text-sm text-gray-700 pl-4 list-disc">
                      {analysis.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {analysis.exclusions && analysis.exclusions.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Ausschlüsse</h4>
                    <ul className="text-sm text-gray-700 pl-4 list-disc">
                      {analysis.exclusions.map((exclusion, index) => (
                        <li key={index}>{exclusion}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {analysis.analysis && (
                  <div>
                    <h4 className="text-sm font-semibold mb-1">Detailanalyse</h4>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{analysis.analysis}</p>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>
      )}
    </div>
  );
};

export default InsuranceComparisonPreview;
